/**
 * Componente LanguageSwitcher
 * 
 * Dropdown para seleção de idioma com bandeiras e persistência automática.
 */

import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/types';
import { cn } from '@/lib/utils';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact';
  className?: string;
}

export function LanguageSwitcher({ variant = 'default', className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  
  // Encontra o idioma atual
  const currentLanguage = SUPPORTED_LANGUAGES.find(
    lang => lang.code === i18n.language
  ) || SUPPORTED_LANGUAGES[0];
  
  // Handler para troca de idioma
  const handleChangeLanguage = (langCode: SupportedLanguage) => {
    i18n.changeLanguage(langCode);
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className={cn(
            "flex items-center gap-1.5 px-2 py-1.5 h-auto",
            "hover:bg-accent/50 transition-colors",
            className
          )}
        >
          <span className="text-base leading-none">{currentLanguage.flag}</span>
          {variant === 'default' && (
            <span className="text-sm font-medium hidden sm:inline">
              {currentLanguage.nativeName}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleChangeLanguage(lang.code)}
            className={cn(
              "flex items-center gap-2.5 cursor-pointer",
              i18n.language === lang.code && "bg-accent"
            )}
          >
            <span className="text-base">{lang.flag}</span>
            <span className="flex-1">{lang.nativeName}</span>
            {i18n.language === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
